import React from 'react';
import {Link} from 'react-router-dom';

import {useQuery} from '@apollo/client/react';
import logoGold from '../assets/img/logos/cway-logo-gold.svg';
import {gql} from 'types/__generated__';

export const USER_COUNTS_QUERY = gql(`
  query DashboardUserCounts {
    userCountsInAnyOrganisation {
      total
      systemAdmins
      earlyAccessProgram
      acceptedTerms
      sso
      agents
      withoutOrganisation
      emailNotVerified
      neverLoggedIn
    }
  }
`);

export const ORGANISATIONS_QUERY = gql(`
  query DashboardOrganisations {
    organisations {
      id
      active
      numberOfUsers
    }
  }
`);

interface UserStatProps {
  title: string;
  value: number | undefined;
  loading: boolean;
  hint: string;
  /** Query string for the user list this count stands for. */
  query?: string;
  valueClass?: string;
}

function UserStat({title, value, loading, hint, query = '', valueClass = 'text-gray-900 dark:text-gray-100'}: UserStatProps) {
  return (
    <Link
      to={`/admin/users${query ? `?${query}` : ''}`}
      className="block bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
    >
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
      <p className={`mt-2 text-3xl font-semibold ${valueClass}`}>{loading ? '...' : (value ?? '–')}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>
    </Link>
  );
}

const Dashboard = () => {
  const {loading, error, data} = useQuery(USER_COUNTS_QUERY, {fetchPolicy: 'cache-and-network'});
  const counts = data?.userCountsInAnyOrganisation;
  const statLoading = loading && !counts;

  const {
    loading: orgsLoading,
    error: orgsError,
    data: orgsData,
  } = useQuery(ORGANISATIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const organisations = orgsData?.organisations || [];
  const totalOrganisations = organisations.length;
  const activeOrganisations = organisations.filter((org: any) => org.active).length;
  const totalOrgUsers = organisations.reduce((sum: number, org: any) => sum + (org.numberOfUsers || 0), 0);

  return (
    <div className="space-y-6">
      {/* Branded header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 rounded-lg shadow-lg p-8 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center mb-2">
              <img src={logoGold} alt="Cway" className="h-24 w-auto mr-4 brightness-0 invert" />
              <h1 className="text-4xl font-bold text-white">Dashboard</h1>
            </div>
            <p className="text-blue-100">Welcome to the Cway Admin Dashboard - Overview of your system</p>
          </div>
        </div>
      </div>

      {/* Organisation Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Organisations</h3>
          <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-gray-100">{orgsLoading ? '...' : totalOrganisations}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{orgsLoading ? '' : `${activeOrganisations} active`}</p>
          {orgsError && <p className="text-xs text-red-600 mt-1">Error loading</p>}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users (from Orgs)</h3>
          <p className="mt-2 text-3xl font-semibold text-blue-600">{orgsLoading ? '...' : totalOrgUsers}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">All users across organisations</p>
          {orgsError && <p className="text-xs text-red-600 mt-1">Error loading</p>}
        </div>
      </div>

      {/* User Stats */}
      {error && <p className="text-sm text-red-600">Error loading user statistics: {error.message}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <UserStat title="Total Users" value={counts?.total} loading={statLoading} hint="All user accounts" />
        <UserStat
          title="Accepted Terms"
          value={counts?.acceptedTerms}
          loading={statLoading}
          hint="Have accepted the terms"
          query="terms=yes"
          valueClass="text-green-600"
        />
        <UserStat title="SSO Users" value={counts?.sso} loading={statLoading} hint="Single Sign-On" query="sso=yes" valueClass="text-blue-600" />
        <UserStat
          title="Early Access"
          value={counts?.earlyAccessProgram}
          loading={statLoading}
          hint="Beta program users"
          query="early=yes"
          valueClass="text-purple-600"
        />
        <UserStat title="System Admins" value={counts?.systemAdmins} loading={statLoading} hint="CWAY system administrators" query="admin=yes" />
        <UserStat
          title="No Organisation"
          value={counts?.withoutOrganisation}
          loading={statLoading}
          hint="Not in any active organisation"
          query="org=none"
          valueClass="text-yellow-600"
        />
        <UserStat
          title="Never Logged In"
          value={counts?.neverLoggedIn}
          loading={statLoading}
          hint="No login in any organisation"
          query="login=never"
          valueClass="text-yellow-600"
        />
        <UserStat
          title="Unverified Email"
          value={counts?.emailNotVerified}
          loading={statLoading}
          hint="Email address never verified"
          query="verified=no"
          valueClass="text-red-600"
        />
        <UserStat title="Agents" value={counts?.agents} loading={statLoading} hint="AI agent accounts" query="agent=yes" />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Welcome to Cway Admin</h2>
        <p className="text-gray-600 dark:text-gray-400">
          This is a modernized version of the Cway Admin dashboard. Components are being migrated to Tailwind CSS.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
